import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, BookOpen, Check, MessageSquare, CalendarDays, TrendingUp } from 'lucide-react';
import { SubjectWithThreads } from '../../types/subjects';
import { buildSubjectDisplayMap } from '../../utils/subjectDisplay';

export type HeaderTab = 'chat' | 'calendar' | 'progress';

interface ChatHeaderProps {
  selectedSubject: string | null;
  userSubjects: SubjectWithThreads[];
  onSubjectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  userInitials: string;
  isProfileOpen: boolean;
  onProfileToggle: () => void;
  activeTab?: HeaderTab;
  onTabChange?: (tab: HeaderTab) => void;
}

const TAB_CONFIG: { id: HeaderTab; label: string; icon: React.ReactNode }[] = [
  { id: 'chat', label: 'Chat', icon: <MessageSquare className="h-3.5 w-3.5" /> },
  { id: 'calendar', label: 'Calendar', icon: <CalendarDays className="h-3.5 w-3.5" /> },
  { id: 'progress', label: 'Progress', icon: <TrendingUp className="h-3.5 w-3.5" /> },
];

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  selectedSubject,
  userSubjects,
  onSubjectChange,
  userInitials,
  isProfileOpen,
  onProfileToggle,
  activeTab = 'chat',
  onTabChange
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const displayNameBySubjectId = useMemo(() => buildSubjectDisplayMap(userSubjects), [userSubjects]);
  const selectedSubjectName = selectedSubject
    ? displayNameBySubjectId[selectedSubject] || userSubjects.find((subject) => subject.subject_id === selectedSubject)?.name || 'Select Subject'
    : 'Select Subject';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubjectSelect = (subjectId: string) => {
    const syntheticEvent = {
      target: { value: subjectId }
    } as React.ChangeEvent<HTMLSelectElement>;
    onSubjectChange(syntheticEvent);
    setIsDropdownOpen(false);
  };

  return (
    <div className="h-14 border-b border-[var(--border-color)] flex items-center justify-between px-4">
      {/* Custom Subject Dropdown */}
      <div data-tour="subject-picker" className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-200
            ${isDropdownOpen 
              ? 'bg-[var(--accent-primary)]/10 border-[var(--accent-primary)] shadow-lg shadow-[var(--accent-primary)]/10' 
              : 'bg-[var(--surface-elevated)] border-[var(--border-color)] hover:border-[var(--accent-primary)]/50 hover:bg-[var(--hover-bg)]'
            }`}
        >
          <BookOpen className={`h-4 w-4 ${selectedSubject ? 'text-[var(--accent-primary)]' : 'text-[var(--text-secondary)]'}`} />
          <span className={`text-sm font-medium ${selectedSubject ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
            {selectedSubjectName}
          </span>
          <ChevronDown className={`h-4 w-4 text-[var(--text-secondary)] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {isDropdownOpen && (
          <div className="absolute top-full left-0 mt-2 w-64 bg-[var(--surface-elevated)] border border-[var(--border-color)] rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            <button
              onClick={() => handleSubjectSelect('')}
              className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-[var(--hover-bg)] transition-colors border-b border-[var(--border-color)]
                ${!selectedSubject ? 'text-[var(--accent-primary)] bg-[var(--accent-primary)]/5' : 'text-[var(--text-secondary)]'}`}
            >
              <span>Select Subject</span>
              {!selectedSubject && <Check className="h-4 w-4" />}
            </button>
            
            <div className="max-h-64 overflow-y-auto">
              {userSubjects.length > 0 ? (
                userSubjects.map((subject) => (
                  <button
                    key={subject.subject_id}
                    onClick={() => handleSubjectSelect(subject.subject_id)}
                    className={`w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-[var(--hover-bg)] transition-colors
                      ${selectedSubject === subject.subject_id 
                        ? 'text-[var(--accent-primary)] bg-[var(--accent-primary)]/5 font-medium' 
                        : 'text-[var(--text-primary)]'}`}
                  >
                    <span>{displayNameBySubjectId[subject.subject_id] || subject.name}</span>
                    {selectedSubject === subject.subject_id && <Check className="h-4 w-4" />}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-[var(--text-secondary)] text-center">
                  No subjects available
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Center Tabs */}
      <div data-tour="top-nav" className="flex items-center gap-1 bg-[var(--bg-secondary)] rounded-lg p-1 border border-[var(--border-color)]">
        {TAB_CONFIG.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange?.(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200
              ${activeTab === tab.id
                ? 'bg-[var(--accent-primary)] text-white shadow-sm'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
              }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Profile Button */}
      <div data-tour="profile-menu" className="relative z-50">
        <button
          onClick={onProfileToggle}
          className="w-9 h-9 rounded-full bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] flex items-center justify-center border-2 border-[var(--accent-primary)]/30 shadow-md transition-all hover:scale-105"
        >
          <span className="text-sm font-medium text-[var(--text-primary)]">{userInitials}</span>
        </button>
      </div>
    </div>
  );
};
